// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getQuoteDetails from "@bg/App/Handlers/CustomQuote/GetCustomQuote";
import resetPdfsettings from "@bg/App/Handlers/PdfGenerator/ClearPdfSettings";
import { CustomAppState } from "@bg/App/Store/Reducers";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import {
    ExcludeFromNavigation,
    HideFromSearchEngines,
    HideFromSiteSearch,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ id?: string }>(search);
    const quoteId = parsedQuery.id;
    return {
        quoteId,
        quoteState: state.myCustomQuoteDetails,
        sortBy: state.myCustomQuoteDetails.sortBy,
        isAuthenticated: getSession(state).isAuthenticated,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        location: getLocation(state),
        myCustomQuotesUrl: getPageLinkByPageType(state, "MyCustomQuotesPage")?.url,
    };
};

const mapDispatchToProps = {
    getQuoteDetails,
    resetPdfsettings,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasShellContext &
    HasToasterContext;

export interface MyCustomQuoteDetailsPageStyles {
    page?: MobiusPageProps;
    loadFailedWrapper?: InjectableCss;
}

export const rfqQuoteDetailsPageStyles: MyCustomQuoteDetailsPageStyles = {
    page: {
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            overflow: initial;
                        `,
                        css`
                            overflow: initial;
                        `,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = rfqQuoteDetailsPageStyles;
const MyCustomQuoteDetailsPage = ({
    id,
    quoteId,
    quoteState,
    getQuoteDetails,
    isAuthenticated,
    shellContext,
    signInUrl,
    location,
    history,
    toaster,
    myCustomQuotesUrl,
    sortBy,
    resetPdfsettings,
}: Props) => {
    const dispatch = useDispatch();
    useEffect(() => {
        if (!isAuthenticated && !shellContext.isInShell) {
            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(location.pathname + location.search)}`);
        }
        return () => {
            dispatch({
                type: "Pages/MyCustomQuotes/ClearLoadedQuote",
            } as any);
        };
    }, []);

    useEffect(() => {
        resetPdfsettings({});
        if (
            quoteId &&
            !quoteState.isLoading &&
            !shellContext.isInShell &&
            !quoteState.errorLoading &&
            isAuthenticated &&
            quoteState.quote?.id !== quoteId
        ) {
            getQuoteDetails({
                quoteId,
                onError: (result: any) => {
                    const error = result;
                    toaster.addToast({
                        body: error,
                        messageType: "danger",
                    });
                    if (myCustomQuotesUrl) {
                        history.push(myCustomQuotesUrl);
                    }
                },
            });
        }
    }, [quoteId, quoteState.isLoading, quoteState.quote, location]);

    return (
        <Page {...styles.page} data-test-selector="MyCustomQuoteDetailsPage">
            <LoadingOverlay loading={quoteState.isRequestingQuote}>
                <Zone contentId={id} zoneName="Content" />
            </LoadingOverlay>
        </Page>
    );
};

const pageModule: PageModule = {
    component: withToaster(
        withIsInShell(connect(mapStateToProps, mapDispatchToProps)(withHistory(MyCustomQuoteDetailsPage))),
    ),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
        fieldDefinitions: [ExcludeFromNavigation, HideFromSearchEngines, HideFromSiteSearch],
    },
};

export default pageModule;

export const MyCustomQuoteDetailsPageContext = "MyCustomQuoteDetailsPage";
